import * as moment from "moment";
import { trace } from "owa-trace";
import RestClient from "sh-rest-client";
import PollHelper from "sh-application/utility/PollHelper";
import { JobStatus } from "sh-models/job/SmartScheduleGenerationJobStatusEntity";
import { updateAssignOpenShiftsJobStatus, clearAssignOpenShiftsJob, setShowSuccessToast, setPartialSuccessToast, setShowFailureToast } from "sh-application/components/autoSchedule/mutatorActions/assignOpenShiftsPollingActions";
import type { UndoData } from "sh-application/components/autoSchedule/mutatorActions/assignOpenShiftsPollingActions";
import DataServices from "../dataservice/AllDataServicesInstance";
import schedulesViewStateStore from "sh-application/components/schedules/lib/store/store";
import { IOpenShiftEntity } from "sh-models";
import ShiftUtils from "sh-application/utility/ShiftUtils";

const TERMINAL_STATUSES: JobStatus[] = [JobStatus.Success, JobStatus.Failure, JobStatus.Canceled];
const POLL_INTERVAL_MS = 5_000;

/**
 * Polls GET /api/jobs/teams/{teamId}/autogeneration/tags/status every 5 seconds for a specific tag.
 * Stops automatically when the job reaches a terminal status (Success/Failure/Canceled).
 */
class AssignOpenShiftsPollingService {
    private _pollHelper: PollHelper | undefined;
    private _isPolling: boolean = false;

    public startPolling(teamId: string, tagId: string): void {
        this.stopPolling();
        this._isPolling = true;

        const performPoll = async (): Promise<void> => {
            if (!this._isPolling) { return; }
            try {
                const response = await RestClient.getAutoGenerationTagsStatus(teamId);
                // Guard against cancel being clicked while the request was in-flight:
                // stopPolling() sets _isPolling = false synchronously, so if Cancel was
                // clicked before this response arrived we bail out here and skip GDIDR.
                if (!this._isPolling) { return; }
                const jobs: any[] = response?.data?.jobs || [];
                const job = jobs.find((j: any) => j.tagId === tagId);
                if (job?.status) {
                    updateAssignOpenShiftsJobStatus(job.status as JobStatus);
                    if (TERMINAL_STATUSES.includes(job.status as JobStatus)) {
                        this.stopPolling();
                        if (job.status === JobStatus.Failure) {
                            setShowFailureToast(tagId, teamId);
                        } else if (job.status === JobStatus.Success) {
                            // Snapshot slot counts and full entities for assignable open shifts in this
                            // tag before refreshing. Mirror the dialog's counting rules:
                            // 1. Draft or published (isPublished is not filtered) — same as dialog group count.
                            // 2. state === Active only — deleted shifts still present in the store are excluded.
                            // 3. Tag matched via tagIds[0] (ShiftUtils.getTagIdFromShift) — same as dialog and schedule view.
                            // Use openSlots (the live count); sharedChanges.openSlots is the last-published
                            // snapshot and does not update when the auto-assign job fills slots.
                            const originalSlotsByShiftId = new Map<string, number>();
                            Array.from(schedulesViewStateStore().openShiftsInView.values()).forEach((os: IOpenShiftEntity) => {
                                if (
                                    ShiftUtils.isActiveShift(os) &&
                                    ShiftUtils.getTagIdFromShift(os) === tagId &&
                                    os.openSlots > 0
                                ) {
                                    originalSlotsByShiftId.set(os.id, os.openSlots);
                                }
                            });

                            const preRefreshViewState = schedulesViewStateStore();
                            const undoData: UndoData = {
                                tagId,
                                teamId,
                                startDate: moment(preRefreshViewState.viewStartDate).utc().format("YYYY-MM-DD"),
                                endDate: moment(preRefreshViewState.viewEndDate).utc().format("YYYY-MM-DD")
                            };

                            try {
                                await DataServices.TeamDataService.getDataInDateRange(
                                    teamId,
                                    preRefreshViewState.viewStartDate,
                                    preRefreshViewState.viewEndDate,
                                    true /* forceLoadFromNetwork */
                                );
                            } catch (e) {
                                trace.warn(`AssignOpenShiftsPollingService: data refresh error: ${e?.message}`);
                            }

                            // Calculate assigned vs total slots to determine full vs partial success.
                            // Use openSlots post-refresh for the same reason as above.
                            const refreshedView = schedulesViewStateStore();
                            let totalSlots = 0;
                            let assignedSlots = 0;
                            originalSlotsByShiftId.forEach((originalSlots, shiftId) => {
                                totalSlots += originalSlots;
                                const refreshed = refreshedView.openShiftsInView.get(shiftId) as IOpenShiftEntity | undefined;
                                // Do not guard on isPublished here: after auto-assign the entity may transition
                                // to a draft state (isPublished = false) even though slots weren't filled.
                                // If the entity is found by ID, its openSlots reflects the actual remaining count.
                                // If not found (all slots filled, entity removed), remainingSlots = 0 is correct.
                                const remainingSlots = refreshed ? refreshed.openSlots : 0;
                                assignedSlots += (originalSlots - remainingSlots);
                            });

                            const isPartial = totalSlots > 0 && assignedSlots < totalSlots;
                            if (isPartial) {
                                setPartialSuccessToast(assignedSlots, totalSlots);
                            } else {
                                setShowSuccessToast(true, undoData, assignedSlots);
                            }
                        }
                        clearAssignOpenShiftsJob();
                    }
                }
            } catch (e) {
                trace.warn(`AssignOpenShiftsPollingService polling error: ${e?.message}`);
            }
        };

        this._pollHelper = new PollHelper(POLL_INTERVAL_MS, POLL_INTERVAL_MS, performPoll);
    }

    public stopPolling(): void {
        if (this._pollHelper) {
            this._pollHelper.stopPolling();
            this._pollHelper = undefined;
        }
        this._isPolling = false;
    }
}

const service = new AssignOpenShiftsPollingService();
export default service as AssignOpenShiftsPollingService;
